import { useState, useEffect } from "react";
import axios from "axios";
import {
  Key,
  Plus,
  Copy,
  Trash2,
  Loader2,
  Eye,
  EyeOff,
  Clock,
  CheckCircle2,
  AlertCircle,
  Code,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function APIKeysManager({ userPlan }) {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const isBusinessPlan = userPlan === "business";

  useEffect(() => {
    if (isBusinessPlan) {
      fetchApiKeys();
    } else {
      setLoading(false);
    }
  }, [isBusinessPlan]);

  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error("Error fetching API keys:", error);
      toast.error("Error al cargar las API Keys");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Ingresa un nombre para la API Key");
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API}/api-keys`,
        { name: newKeyName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewKeyValue(response.data.key);
      setShowCreateDialog(false);
      setShowNewKeyDialog(true);
      setNewKeyName("");
      fetchApiKeys();
    } catch (error) {
      console.error("Error creating API key:", error);
      toast.error(error.response?.data?.detail || "Error al crear la API Key");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("API Key revocada exitosamente");
      fetchApiKeys();
    } catch (error) {
      console.error("Error deleting API key:", error);
      toast.error("Error al revocar la API Key");
    }
    setKeyToDelete(null);
  };

  const copyToClipboard = (text, keyId = null) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId || "new");
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Nunca";
    return new Date(dateStr).toLocaleDateString("es-CR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Not Business plan - show upgrade prompt
  if (!isBusinessPlan) {
    return (
      <Card className="border-dashed border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
        <CardContent className="py-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mx-auto">
              <Key className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">API Keys - Plan Business</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Integra TalentMatcher con tus sistemas mediante nuestra API REST. 
                Automatiza la carga de CVs, obtén resultados programáticamente y más.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600" />
                <span>REST API completa</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600" />
                <span>1,000 requests/día</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600" />
                <span>Documentación incluida</span>
              </div>
            </div>
            <p className="text-xs text-amber-600 font-medium">
              Actualiza a Business para desbloquear el acceso API
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Key className="w-5 h-5 text-primary" />
                API Keys
              </CardTitle>
              <CardDescription>
                Integra TalentMatcher con tus sistemas • {apiKeys.filter(k => k.is_active).length}/5 keys activas
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDocsDialog(true)}>
                <Code className="w-4 h-4 mr-1" />
                Docs
              </Button>
              <Button 
                size="sm" 
                onClick={() => setShowCreateDialog(true)}
                disabled={apiKeys.filter(k => k.is_active).length >= 5}
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva Key
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                <Key className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No tienes API Keys creadas</p>
              <Button 
                className="mt-4" 
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear primera API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div 
                  key={key.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    key.is_active 
                      ? "bg-card" 
                      : "bg-muted/50 opacity-60"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      key.is_active ? "bg-primary/10" : "bg-muted"
                    }`}>
                      <Key className={`w-5 h-5 ${key.is_active ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{key.name}</span>
                        {!key.is_active && (
                          <Badge variant="secondary" className="text-xs">Revocada</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <code className="bg-muted px-2 py-0.5 rounded text-xs">
                          {key.key_preview}
                        </code>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(key.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <div className="font-medium">{key.requests_count.toLocaleString()}</div>
                      <div className="text-muted-foreground text-xs">requests</div>
                    </div>
                    {key.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setKeyToDelete(key)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear nueva API Key</DialogTitle>
            <DialogDescription>
              Dale un nombre descriptivo para identificarla fácilmente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Nombre de la Key</Label>
              <Input
                id="key-name"
                placeholder="Ej: Integración ATS, App Móvil, etc."
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                maxLength={50}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateKey} disabled={creating || !newKeyName.trim()}>
              {creating ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creando...</>
              ) : (
                "Crear API Key"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Created Dialog */}
      <Dialog open={showNewKeyDialog} onOpenChange={setShowNewKeyDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              API Key Creada
            </DialogTitle>
            <DialogDescription>
              Copia tu API Key ahora. <strong className="text-destructive">No podrás verla de nuevo.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between gap-2">
                <code className="text-sm font-mono break-all flex-1">{newKeyValue}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(newKeyValue)}
                >
                  {copiedKey === "new" ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Guarda esta clave en un lugar seguro. Por razones de seguridad, 
                  no podrás ver la clave completa de nuevo.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowNewKeyDialog(false)}>
              Entendido, ya la copié
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!keyToDelete} onOpenChange={() => setKeyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revocar API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              La API Key "{keyToDelete?.name}" dejará de funcionar inmediatamente. 
              Cualquier integración que la use dejará de tener acceso.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleDeleteKey(keyToDelete.id)}
            >
              Revocar Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* API Documentation Dialog */}
      <Dialog open={showDocsDialog} onOpenChange={setShowDocsDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              Documentación de la API
            </DialogTitle>
            <DialogDescription>
              Guía rápida para integrar TalentMatcher con tus sistemas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Authentication */}
            <div>
              <h4 className="font-semibold mb-2">🔐 Autenticación</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Incluye tu API Key en el header de cada request:
              </p>
              <div className="bg-muted p-3 rounded-lg">
                <code className="text-sm">Authorization: Bearer tm_tu_api_key...</code>
              </div>
            </div>

            {/* Base URL */}
            <div>
              <h4 className="font-semibold mb-2">🌐 URL Base</h4>
              <div className="bg-muted p-3 rounded-lg">
                <code className="text-sm">{process.env.REACT_APP_BACKEND_URL}/api/v1</code>
              </div>
            </div>

            {/* Endpoints */}
            <div>
              <h4 className="font-semibold mb-2">📡 Endpoints Disponibles</h4>
              <div className="space-y-3">
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">GET</Badge>
                    <code className="text-sm font-medium">/jobs</code>
                  </div>
                  <p className="text-sm text-muted-foreground">Listar todos tus puestos de trabajo</p>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">POST</Badge>
                    <code className="text-sm font-medium">/jobs</code>
                  </div>
                  <p className="text-sm text-muted-foreground">Crear nuevo puesto</p>
                  <code className="text-xs text-muted-foreground block mt-1">
                    Body: {`{ "title": "...", "description": "...", "required_skills": [...] }`}
                  </code>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">POST</Badge>
                    <code className="text-sm font-medium">/jobs/{"{job_id}"}/candidates</code>
                  </div>
                  <p className="text-sm text-muted-foreground">Subir CV para análisis (multipart/form-data)</p>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">GET</Badge>
                    <code className="text-sm font-medium">/jobs/{"{job_id}"}/candidates</code>
                  </div>
                  <p className="text-sm text-muted-foreground">Listar candidatos de un puesto con scores</p>
                </div>
              </div>
            </div>

            {/* Example */}
            <div>
              <h4 className="font-semibold mb-2">💻 Ejemplo con cURL</h4>
              <div className="bg-zinc-900 text-zinc-100 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm whitespace-pre-wrap">
{`# Listar puestos
curl -H "Authorization: Bearer tm_tu_api_key" \\
  ${process.env.REACT_APP_BACKEND_URL}/api/v1/jobs

# Subir CV
curl -X POST \\
  -H "Authorization: Bearer tm_tu_api_key" \\
  -F "file=@curriculum.pdf" \\
  ${process.env.REACT_APP_BACKEND_URL}/api/v1/jobs/{job_id}/candidates`}
                </pre>
              </div>
            </div>

            {/* Rate Limits */}
            <div>
              <h4 className="font-semibold mb-2">⚡ Rate Limits</h4>
              <p className="text-sm text-muted-foreground">
                Cada API Key tiene un límite de <strong>1,000 requests por día</strong>. 
                El contador se reinicia a medianoche UTC.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocsDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
